import React, { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { Button, Switch, Text, TextInput } from 'react-native-paper';
import { useTranslation } from 'react-i18next';

import { ResumeExperienceItem } from '../../../types';
import { Palette, spacing, useThemePalette } from '../../../theme';
import RepeatableItemCard from '../RepeatableItemCard';
import DateField from '../DateField';

interface Props {
  value: ResumeExperienceItem[];
  onChange: (next: ResumeExperienceItem[]) => void;
}

const makeId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const blankItem = (): ResumeExperienceItem => ({
  id: makeId(),
  company: '',
  position: '',
  location: '',
  startDate: '',
  endDate: '',
  current: false,
  description: '',
});

const ExperienceForm: React.FC<Props> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const palette = useThemePalette();
  const styles = useMemo(() => makeStyles(palette), [palette]);

  const update = (id: string, patch: Partial<ResumeExperienceItem>) =>
    onChange(value.map((it) => (it.id === id ? { ...it, ...patch } : it)));

  const remove = (id: string) => onChange(value.filter((it) => it.id !== id));

  return (
    <View style={styles.container}>
      {value.map((item, idx) => (
        <RepeatableItemCard
          key={item.id}
          title={
            item.position ||
            item.company ||
            `${t('resume.sections.experience')} ${idx + 1}`
          }
          onRemove={() => remove(item.id)}
        >
          <TextInput
            mode="outlined"
            label={t('resume.fields.position')}
            value={item.position}
            onChangeText={(v) => update(item.id, { position: v })}
            dense
          />
          <TextInput
            mode="outlined"
            label={t('resume.fields.company')}
            value={item.company}
            onChangeText={(v) => update(item.id, { company: v })}
            dense
          />
          <TextInput
            mode="outlined"
            label={t('resume.fields.location')}
            value={item.location}
            onChangeText={(v) => update(item.id, { location: v })}
            dense
          />
          <View style={styles.row}>
            <View style={styles.col}>
              <DateField
                label={t('resume.fields.startDate')}
                value={item.startDate}
                onChange={(v) => update(item.id, { startDate: v })}
              />
            </View>
            <View style={styles.col}>
              <DateField
                label={t('resume.fields.endDate')}
                value={item.endDate}
                onChange={(v) => update(item.id, { endDate: v })}
                disabled={item.current}
                placeholder={item.current ? t('resume.fields.current') : '—'}
              />
            </View>
          </View>
          <View style={styles.switchRow}>
            <Text style={styles.switchLabel}>{t('resume.fields.current')}</Text>
            <Switch
              value={item.current}
              onValueChange={(v) =>
                update(item.id, { current: v, endDate: v ? '' : item.endDate })
              }
              color={palette.primary}
            />
          </View>
          <TextInput
            mode="outlined"
            label={t('resume.fields.description')}
            value={item.description}
            onChangeText={(v) => update(item.id, { description: v })}
            multiline
            numberOfLines={3}
            style={styles.multiline}
          />
        </RepeatableItemCard>
      ))}
      <Button
        mode="outlined"
        icon="plus"
        onPress={() => onChange([...value, blankItem()])}
        textColor={palette.textPrimary}
        style={styles.addBtn}
      >
        {t('resume.buttons.addExperience')}
      </Button>
    </View>
  );
};

const makeStyles = (p: Palette) =>
  StyleSheet.create({
    container: { gap: spacing.sm },
    row: { flexDirection: 'row', gap: spacing.sm },
    col: { flex: 1 },
    switchRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingVertical: spacing.xs,
    },
    switchLabel: { color: p.textPrimary, fontSize: 14 },
    multiline: { minHeight: 80 },
    addBtn: { borderRadius: 999, borderColor: p.border, marginTop: spacing.xs },
  });

export default ExperienceForm;
